import React from "react";
import "./MappingField.scss";
import { getTicketingIcon } from "../../TicketingUtils";
import MappingFieldRow from "./MappingFieldRow/MappingFieldRow";
import { fieldTypeOptions } from "../GenericField";
import type { FieldErrors, FieldValues, UseFormSetValue } from "react-hook-form";

interface MappingFieldColumn {
  icon?: string;
  label?: string;
}

export interface MappingFieldColumns {
  field_from?: MappingFieldColumn;
  field_to?: MappingFieldColumn;
}

interface MappingFieldEndpoint {
  input?: string;
  name?: string;
  label?: string;
}

export interface MappingFieldRowData {
  field_from?: MappingFieldEndpoint;
  field_to?: MappingFieldEndpoint;
}

export type MappingFieldValue = Record<string, unknown>;

export interface MappingFieldProps {
  columns?: MappingFieldColumns;
  data?: MappingFieldRowData[];
  value?: MappingFieldValue;
  label?: React.ReactNode;
  isUnique?: boolean;
  description?: React.ReactNode;
  setValue: UseFormSetValue<FieldValues>;
  registerPrefix: string;
  formState: { errors: FieldErrors };
}

const MappingField: React.FC<MappingFieldProps> = (props) => {
  const {
    columns,
    data,
    value,
    label,
    isUnique,
    description,
    setValue,
    registerPrefix,
    formState: { errors },
  } = props;

  const getMappingFieldTitle = (
    input: string | undefined,
    label: string | undefined,
    icon: string | undefined
  ) => {
    // `icon` may be undefined; passed through verbatim (getTicketingIcon
    // returns undefined for missing icons, guarded by `!!ticketingIcon` below).
    const ticketingIcon = getTicketingIcon(icon as string, "S");
    return (
      <div
        className={`mapping-field-cell-${input === fieldTypeOptions.LABEL ? "label" : "input"} mapping-cell mapping-title mapping-title-above-inputs`}
      >
        {!!ticketingIcon && (
          <img
            className={"mapping-cell-img from-icon"}
            alt={label}
            src={ticketingIcon} />
        )}
        <div className="mapping-cell-text">{label}</div>
        <span className={"label-helper label-helper-in-wrap"}>Optional</span>
      </div>
    );
  };

  const mappedFields = Object.values(value || {}).reduce<unknown[]>(
    (acc, mappedValue) => {
      if (Array.isArray(mappedValue) && isUnique) {
        return [...acc, ...mappedValue];
      } else if (isUnique) {
        return [...acc, mappedValue];
      }

      return acc;
    },
    []
  );

  const shouldDisplayLabelTag =
    !!columns?.field_from?.icon && !!columns?.field_to?.icon;

  return (
    <div className={"mapping-field"}>
      {label && (
        <>
          <div className={"mapping-label"}>
            {shouldDisplayLabelTag && (
              <div className={"mapping-label-tag"}>
                <img
                  className={"mapping-cell-img"}
                  alt={columns?.field_from?.label}
                  src={getTicketingIcon(columns?.field_from?.icon as string, "S")}
                />
                <span className="mapping-field-arrow"><i className="seem-icon seem-icon-next-arrow" /></span>
                <img
                  className={"mapping-cell-img"}
                  alt={columns?.field_to?.label}
                  src={getTicketingIcon(columns?.field_to?.icon as string, "S")}
                />
              </div>
            )}
            {label}
          </div>
          <div className={"mapping-separator"} />
          {description && (
            <div className={"mapping-field-description"}>{description}</div>
          )}
        </>
      )}
      {!!data && (
        <>
          <div
            className={
              "mapping-field-row mapping-title mapping-title-above-labels"
            }
          >
            <div className={"mapping-field-cell-from"}>
              {getMappingFieldTitle(
                data?.[0]?.field_from?.input,
                columns?.field_from?.label,
                columns?.field_from?.icon
              )}
            </div>
            <div className={"mapping-field-cell-to"}>
              {getMappingFieldTitle(
                data?.[0]?.field_to?.input,
                columns?.field_to?.label,
                columns?.field_to?.icon
              )}
            </div>
          </div>
          <ul>
            {data?.map((row, index) => {
              return (
                <li key={`${row?.field_from?.name}-${index}`}>
                  <MappingFieldRow
                    {...props}
                    row={row}
                    value={value}
                    // `mappedFields` is reduced as unknown[]; the row treats
                    // these entries as option ids (strings) at runtime.
                    mappedFields={mappedFields as string[]}
                    onRowChange={(val: MappingFieldValue) => {
                      // We're using setValue instead of onChange to update the value of the field because it allows us to control
                      // whether or not the field should be validated when the value changes. By default, React
                      // Hook Form will trigger validation on every value change, which can be undesirable in some
                      // cases. By using setValue instead of onChange with the shouldValidate option set to false, we can update the
                      // value without triggering validation, therefore the mappingRow errors won't be removed from errors object.
                      setValue(registerPrefix, val, { shouldValidate: false });
                    }}
                    errors={errors}
                    registerPrefix={registerPrefix}
                  />
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};

export default MappingField;
